import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { TrackDetailView, Toast, useToast, ConfirmationModal, useConfirmation, RejectModal } from '../../components/shared';
import { useTranslation } from 'react-i18next';
import { Release } from '../../types';

const AdminTrackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { releases, deleteRelease, updateReleaseStatus } = useData();
  const { t } = useTranslation();
  const { toasts, hideToast, showError, showSuccess } = useToast();
  const { confirmationState, showConfirmation, handleCancel } = useConfirmation();
  const navigate = useNavigate();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const release = releases.find(r => r.id === id);

  if (!release) {
    return (
      <div className="glass-card text-center py-12">
        <h2 className="text-2xl font-bold text-white">{t('tracks.notFound')}</h2>
        <p className="mt-2 text-white/70">{t('tracks.noAccess')}</p>
        <Link to="/admin/releases" className="mt-4 btn-primary inline-block">
          {t('admin.backToAllMusic')}
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    const confirmed = await showConfirmation({
      title: t('admin.deleteConfirmTitle'),
      message: t('admin.deleteConfirmMessage'),
      confirmText: t('common.delete'),
      type: 'danger'
    });

    if (confirmed) {
      const success = await deleteRelease(release.id);
      if (success) {
        showSuccess(t('admin.deleteSuccess'));
        navigate('/admin/releases');
      } else {
        showError(t('admin.deleteError'));
      }
    }
  };

  const handleStatusChange = async (newStatus: Release['status']) => {
    if (!release) return;

    // If rejected, show reject modal
    if (newStatus === 'rejected') {
      setRejectModalOpen(true);
      return;
    }

    // Show confirmation for status change
    const confirmed = await showConfirmation({
      title: 'Status Değiştir',
      message: `"${release.title}" adlı şarkının statusunu "${getStatusText(newStatus)}" olarak değiştirmek istediğinize emin misiniz?`,
      confirmText: 'Evet, Değiştir',
      type: 'info'
    });

    if (confirmed) {
      const success = await updateReleaseStatus(release.id, newStatus);
      if (success) {
        showSuccess('Status başarıyla güncellendi!');
      } else {
        showError('Status güncellenirken hata oluştu.');
      }
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!release) return;

    setRejectModalOpen(false);
    const success = await updateReleaseStatus(release.id, 'rejected', reason);
    if (success) {
      showSuccess('Şarkı reddedildi.');
    } else {
      showError('Reddetme işlemi sırasında hata oluştu.');
    }
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return t('status.draft');
      case 'pending':
        return t('status.pending');
      case 'approved':
        return t('status.approved');
      case 'submitted':
        return t('status.submitted');
      case 'published':
        return t('status.published');
      case 'rejected':
        return t('status.rejected');
      default:
        return status;
    }
  };

  // Admin her zaman edit edebilir
  const canEdit = true;
  
  // Admin taslak parçaları silebilir
  const canDelete = release.status === 'draft';

  return (
    <>
      <TrackDetailView
        release={release}
        isAdmin={true}
        canEdit={canEdit}
        canDelete={canDelete}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        backToPath="/admin/releases"
        backToText="admin.backToAllMusic"
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        onConfirm={confirmationState.onConfirm}
        onCancel={handleCancel}
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={rejectModalOpen}
        releaseTitle={release?.title || ''}
        onConfirm={handleRejectConfirm}
        onCancel={handleRejectCancel}
      />

      {/* Toast Messages */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </>
  );
};

export default AdminTrackDetail; 